import * as DrawerPrimitive from "@radix-ui/react-dialog"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { DialogClose } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

export const DrawerTrigger = DrawerPrimitive.Trigger
export const DrawerClose = DialogClose

interface DrawerContentProps extends React.ComponentPropsWithRef<typeof DrawerPrimitive.Content> {
  hasAnimation?: boolean
}

export const Drawer = ({ ...props }: Omit<React.ComponentPropsWithRef<typeof DrawerPrimitive.Root>, "modal">) => (
  <DrawerPrimitive.Root modal={false} {...props} />
)

export const DrawerContent = ({ className, children, hasAnimation = true, ref, ...props }: DrawerContentProps) => (
  <>
    <DrawerPrimitive.Portal>
      <DrawerPrimitive.Overlay />
    </DrawerPrimitive.Portal>
    <DrawerPrimitive.Content
      aria-describedby=""
      ref={ref}
      className={cn(
        "bg-surface shadow-drawer relative h-full w-125 rounded-l-xl",
        hasAnimation &&
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
        className,
      )}
      onInteractOutside={(e) => e.preventDefault()}
      onPointerDownOutside={(e) => e.preventDefault()}
      onCloseAutoFocus={(e) => e.preventDefault()}
      onOpenAutoFocus={(e) => e.preventDefault()}
      {...props}>
      <VisuallyHidden asChild>
        <DrawerPrimitive.Title>Hidden Title</DrawerPrimitive.Title>
      </VisuallyHidden>
      <div className="absolute inset-y-0 right-0 flex h-full w-full flex-col">{children}</div>
    </DrawerPrimitive.Content>
  </>
)

type DrawerSectionsProps = React.HTMLAttributes<HTMLDivElement>

export const DrawerHeader = ({ className, ...props }: DrawerSectionsProps) => (
  <div className={cn("border-border flex flex-col border-b p-4 pr-2", className)} {...props} />
)

export const DrawerBody = ({ className, ...props }: DrawerSectionsProps) => (
  <div className={cn("flex flex-1 flex-col overflow-auto p-4", className)} {...props} />
)

export const DrawerFooter = ({ className, ...props }: DrawerSectionsProps) => (
  <div className={cn("border-border flex flex-row border-t p-4", className)} {...props} />
)

export const DrawerTitle = ({ className, ...props }: React.ComponentPropsWithRef<typeof DrawerPrimitive.Title>) => (
  <DrawerPrimitive.Title className={cn("text-on-surface pr-14 text-xl font-bold", className)} {...props} />
)

export const DrawerDescription = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof DrawerPrimitive.Description>) => (
  <DrawerPrimitive.Description className={cn("text-on-surface-subdued text-sm", className)} {...props} />
)
